export type AdultingCommunityView = 'channels' | 'rooms' | 'messages';

export type AdultingCommunityChannelKey =
  | 'general'
  | 'money'
  | 'home'
  | 'health'
  | 'family'
  | 'work'
  | 'self-employed'
  | 'neurospicy'
  | 'carers'
  | 'accountability'
  | 'local'
  | 'feedback';

export type AdultingCommunityChannelGroup = 'app-areas' | 'support';

export type AdultingCommunityChannelMeta = {
  key: AdultingCommunityChannelKey;
  label: string;
  emoji: string;
  description: string;
  detailTitle: string;
  detailSubtitle: string;
  group: AdultingCommunityChannelGroup;
};

export const ADULTING_COMMUNITY_CHANNEL_GROUPS: Array<{
  key: AdultingCommunityChannelGroup;
  title: string;
  subtitle: string;
}> = [
  {
    key: 'app-areas',
    title: 'App areas',
    subtitle: '',
  },
  {
    key: 'support',
    title: 'Extra support',
    subtitle: '',
  },
];

export const ADULTING_COMMUNITY_CHANNELS: AdultingCommunityChannelMeta[] = [
  {
    key: 'general',
    label: 'General',
    emoji: '💬',
    description: 'Broad life-admin support that does not fit one section cleanly.',
    detailTitle: 'General channel',
    detailSubtitle: 'For broad Adulting questions, shared scripts, and the bits of life admin that spill across sections.',
    group: 'app-areas',
  },
  {
    key: 'money',
    label: 'Money',
    emoji: '💸',
    description: 'Benefits, bills, debt, budgeting, and money pressure.',
    detailTitle: 'Money channel',
    detailSubtitle: 'For benefits, bills, debt, budgeting, and the parts of money admin that feel heavy fastest.',
    group: 'app-areas',
  },
  {
    key: 'home',
    label: 'Home',
    emoji: '🏠',
    description: 'Repairs, renewals, moving, tenancy, and household admin.',
    detailTitle: 'Home channel',
    detailSubtitle: 'For repairs, renewals, moving, tenancy questions, and the home admin that spreads everywhere.',
    group: 'app-areas',
  },
  {
    key: 'health',
    label: 'Health',
    emoji: '🩺',
    description: 'Appointments, repeat prescriptions, admin, and wellbeing crossover.',
    detailTitle: 'Health channel',
    detailSubtitle: 'For appointments, repeat prescriptions, admin chasing, and the practical side of staying on top of health.',
    group: 'app-areas',
  },
  {
    key: 'family',
    label: 'People',
    emoji: '👨‍👩‍👧',
    description: 'School, childcare, co-parenting, family admin, and caring overlap.',
    detailTitle: 'People channel',
    detailSubtitle: 'For school, childcare, co-parenting, family admin, and the people load that sits beside everything else.',
    group: 'app-areas',
  },
  {
    key: 'work',
    label: 'Work',
    emoji: '💼',
    description: 'Employment rights, deadlines, difficult work admin, and work pressure.',
    detailTitle: 'Work channel',
    detailSubtitle: 'For employment rights, deadlines, difficult work admin, and the work pressure that leaks into the rest of life.',
    group: 'app-areas',
  },
  {
    key: 'self-employed',
    label: 'Self-employment',
    emoji: '📒',
    description: 'Invoices, HMRC, chasing, project admin, and feast-or-famine pressure.',
    detailTitle: 'Self-employment channel',
    detailSubtitle: 'For invoices, HMRC admin, chasing, and the self-employed load that never really clocks off.',
    group: 'app-areas',
  },
  {
    key: 'neurospicy',
    label: 'Neurodivergence',
    emoji: '🧠',
    description: 'ADHD, autism, overwhelm, sensory load, and admin friction.',
    detailTitle: 'Neurodivergence channel',
    detailSubtitle: 'For ADHD, autism, overwhelm, sensory friction, and the ways adulting systems clash with neurodivergent brains.',
    group: 'support',
  },
  {
    key: 'carers',
    label: 'Carers',
    emoji: '🫶',
    description: 'Caring, respite, carer admin, and the load of carrying too much.',
    detailTitle: 'Carers channel',
    detailSubtitle: 'For caring, respite, practical scripts, and the hidden admin load of holding things together for someone else.',
    group: 'support',
  },
  {
    key: 'accountability',
    label: 'Accountability',
    emoji: '🪴',
    description: 'Gentle check-ins, progress nudges, and done-enough accountability.',
    detailTitle: 'Accountability channel',
    detailSubtitle: 'For gentle check-ins, honest progress updates, and saying what you want done-enough to look like this week.',
    group: 'support',
  },
  {
    key: 'local',
    label: 'Local',
    emoji: '📍',
    description: 'Approximate local support, routes, and area-based practical help.',
    detailTitle: 'Local channel',
    detailSubtitle: 'For approximate local support, trusted routes, and area-based practical help without oversharing location detail.',
    group: 'support',
  },
  {
    key: 'feedback',
    label: 'Adulting feedback',
    emoji: '💡',
    description: 'What to fix, improve, or build next in Adulting.',
    detailTitle: 'Adulting feedback channel',
    detailSubtitle: 'For what feels broken, what would help, and what Adulting should change next.',
    group: 'support',
  },
];

export function getAdultingCommunityChannel(key: string) {
  return ADULTING_COMMUNITY_CHANNELS.find((channel) => channel.key === key) || ADULTING_COMMUNITY_CHANNELS[0];
}
